import { useFlags } from '@atlaskit/flag';
import { ProcessTypesApiService } from 'core/api';
import { useDialog } from 'core/providers/DialogProvider';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useCollection } from 'core/hooks/useCollection';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { PageElement } from 'core/utilities/interface-helpers';
import { QueryConstraint, where } from 'firebase/firestore';
import { IButton } from 'shared/components/buttons/button-interface';
import { IProcessTypeDao } from 'core/api/types/process-types.interface';
import AddEditProcessTypeDialog from './dialogs/add-edit-process-type-dialog';

const ProcessTypesTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Name', key: 'name', templateId: 'text', width: 10 },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const ProcessTypesList = () => {
  const dialog = useDialog();
  const flags = useFlags();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IProcessTypeDao>({
    apiService: ProcessTypesApiService,
    constraints,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '' && !filter.id.includes('.'))
        .map((filter) => {
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (processType: IProcessTypeDao) => {
    return (
      <Checkbox isChecked={selectedRows.includes(processType.uid)} onChange={(e) => rowChecked(e, processType.uid)} />
    );
  };

  const actionCellTemplate = (processType: IProcessTypeDao) => {
    return (
      <div className='w-full flex justify-end space-x-2'>
        <SharedButton
          onClick={() => dialog?.openDialog(<AddEditProcessTypeDialog processType={processType} />)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Update'
        />

        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ConfirmationDialog
                title='Delete process type'
                textContent='Are you sure you want to delete this process type? This action cannot be undone.'
                action={() => deleteItem(ProcessTypesApiService, processType, flags, dialog)}
                buttonLabel='Delete Process Type'
              />
            )
          }
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Delete'
        />
      </div>
    );
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    { template: selectCellTemplate, id: 'select' },
    {
      template: actionCellTemplate,
      id: 'action',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const headerActions: PageElement[] = [
    ...(selectedRows.length
      ? [
          {
            key: 'deleteSettingsProcessTypes',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete process types'
                      textContent='Are you sure you want to delete these process types? This action cannot be undone.'
                      action={() => deleteItems(ProcessTypesApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Process Types'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Process Types'
              />
            ),
          },
        ]
      : [
          {
            key: 'addSettingsProcessType',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<AddEditProcessTypeDialog />)}
                type='button'
                appearance='primary'
                label='Add Process Type'
              />
            ),
          },
        ]),
  ];

  const filters: Filter[] = [
    {
      id: 'active',
      label: 'Active',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Process Types' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={ProcessTypesTableColumns}
        customTemplates={customTemplates}
        rows={tableRows}
        isLoading={loading}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default ProcessTypesList;
